import axios from 'axios'
import { getIdToken } from '@/utils/auth'
import { API_BASE } from '@/services/api'

/** Initiate Google OAuth flow (usually just redirect) */
export const getGoogleOAuthUrl = (enableRefresh: boolean = false) => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  const redirectUri = import.meta.env.DEV
    ? 'http://localhost:5173/oauth/callback'
    : import.meta.env.VITE_ENV === 'staging'
      ? 'https://appstg.getbanksheets.com/oauth/callback'
      : 'https://app.getbanksheets.com/oauth/callback'
  const scope = ['https://www.googleapis.com/auth/drive.file'].join(' ')

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope,
    ...(enableRefresh && { access_type: 'offline', prompt: 'consent' }),
  })

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
}

/** After redirect, exchange Google auth code for access + refresh tokens */
export const oauthCallback = async (code: string): Promise<void> => {
  const token = await getIdToken()
  await axios.get(`${API_BASE}/oauthCallback`, {
    params: { code },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

/** Create or append to a spreadsheet */
export const createSpreadsheet = async ({
  sheetSelection,
  spreadsheetTitle,
  existingSpreadsheetId,
  existingSheetTab,
  exportType,
  folderId,
  transactions,
  selectedAccountIds,
  enableAutomaticSyncing,
  syncFrequency,
  templateId,
}: {
  sheetSelection: 'new' | 'existing'
  spreadsheetTitle?: string
  existingSpreadsheetId?: string
  existingSheetTab?: string
  exportType: 'monthly' | 'accounts' | 'single'
  folderId?: string
  transactions: {
    date: string
    name: string
    amount: number
    account_mask?: string
    account_name?: string
    direction: string
    original_description?: string
    primary_category?: string
    detailed_category?: string
    pending: boolean
    merchant_name?: string
    payment_channel?: string
    ai_category?: string
    ai_confidence?: number
  }[]
  selectedAccountIds?: string[]
  enableAutomaticSyncing?: boolean
  syncFrequency?: string
  templateId?: string
}): Promise<{ spreadsheetId: string; spreadsheetUrl?: string }> => {
  const token = await getIdToken()
  const { data } = await axios.post(
    `${API_BASE}/createSpreadsheet`,
    {
      sheetSelection,
      spreadsheetTitle,
      existingSpreadsheetId,
      existingSheetTab,
      exportType,
      folderId,
      transactions,
      selectedAccountIds,
      enableAutomaticSyncing,
      syncFrequency,
      templateId,
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )

  return data
}

/** Append transaction data to spreadsheet */
export const appendTransactionsToSheet = async ({
  spreadsheetId,
  transactions,
  exportType,
  lastSyncedTabs,
}: {
  spreadsheetId: string
  transactions: {
    date: string
    name: string
    amount: number
    account_mask?: string
    account_name?: string
    direction: string
    original_description?: string
    primary_category?: string
    detailed_category?: string
    pending: boolean
    merchant_name?: string
    payment_channel?: string
    ai_category?: string
    ai_confidence?: number
  }[]
  exportType?: 'monthly' | 'accounts' | 'single'
  lastSyncedTabs?: string[]
}) => {
  const token = await getIdToken()
  await axios.post(
    `${API_BASE}/appendTransactions`,
    { spreadsheetId, transactions, exportType, lastSyncedTabs },
    { headers: { Authorization: `Bearer ${token}` } }
  )
}

/** List user's spreadsheets */
export const listUserSpreadsheets = async (): Promise<
  { id: string; title: string; modifiedAt?: string }[]
> => {
  const token = await getIdToken()

  try {
    const { data } = await axios.get(`${API_BASE}/listSpreadsheets`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return data.spreadsheets || []
  } catch (err: unknown) {
    if (
      axios.isAxiosError(err) &&
      err.response?.status === 401 &&
      err.response?.data?.error?.includes('expired')
    ) {
      const params = new URLSearchParams({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        redirect_uri: `${window.location.origin}/oauth/callback`,
        response_type: 'code',
        scope:
          'https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive.readonly',
        prompt: 'consent',
      })

      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
    }

    throw err // fallback for other errors
  }
}
